import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { getPendingGstHotels, updateHotelGstStatus } from '../api/adminPanelApi';

const GST_STATUS_OPTIONS = ['pending', 'active', 'rejected'];

const normalizeGstStatus = (row) => {
  const s = (row?.gst_status || row?.gst_active || 'pending').toString().toLowerCase();
  if (s === 'active') return 'active';
  if (s === 'rejected') return 'rejected';
  return 'pending';
};

const displayGstStatus = (status) => {
  if (status === 'active') return 'Active';
  if (status === 'rejected') return 'Rejected';
  return 'Pending';
};

const getGstStatusColor = (status) => {
  if (status === 'active') return '#2e7d32';
  if (status === 'rejected') return '#d32f2f';
  return '#ed6c02';
};

const rowMatchesSearch = (row, q) => {
  const lower = q.toLowerCase();
  return (
    String(row.hotel_id ?? '').includes(lower) ||
    (row.hotel_name || '').toLowerCase().includes(lower) ||
    (row.gst_number || '').toLowerCase().includes(lower) ||
    (row.email || '').toLowerCase().includes(lower) ||
    (row.phone || '').toLowerCase().includes(lower) ||
    (row.city_name || '').toLowerCase().includes(lower)
  );
};

const GstStatus = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchList = useCallback(async ({ notify = false } = {}) => {
    setLoading(true);
    try {
      const res = await getPendingGstHotels();
      if (res.success) {
        setList(res.hotels || []);
        if (notify) {
          setSnackbar({
            open: true,
            message: `Loaded ${res.hotels?.length ?? 0} pending hotel(s)`,
            severity: 'success',
          });
        }
      } else {
        setList([]);
        setSnackbar({
          open: true,
          message: res.error || 'Failed to load pending GST hotels',
          severity: 'error',
        });
      }
    } catch (e) {
      setList([]);
      setSnackbar({ open: true, message: e.message || 'Failed to load', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const filteredList = useMemo(() => {
    const q = searchTerm.trim();
    if (!q) return list;
    return list.filter((row) => rowMatchesSearch(row, q));
  }, [list, searchTerm]);

  const handleStatusChange = async (row, newStatus) => {
    const hotelId = row.hotel_id;
    const current = normalizeGstStatus(row);
    if (!hotelId || !GST_STATUS_OPTIONS.includes(newStatus) || newStatus === current) return;

    setUpdatingId(hotelId);
    try {
      const res = await updateHotelGstStatus(hotelId, newStatus);
      if (res.success) {
        setSnackbar({
          open: true,
          message: res.message || `GST status updated to "${displayGstStatus(newStatus)}"`,
          severity: 'success',
        });
        if (newStatus === 'active' || newStatus === 'rejected') {
          setList((prev) => prev.filter((r) => r.hotel_id !== hotelId));
        } else {
          setList((prev) =>
            prev.map((r) =>
              r.hotel_id === hotelId
                ? { ...r, gst_active: displayGstStatus(newStatus), gst_status: newStatus }
                : r
            )
          );
        }
      } else {
        setSnackbar({ open: true, message: res.error || 'Failed to update GST status', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to update GST status', severity: 'error' });
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/')} sx={{ color: '#FF7700', '&:hover': { backgroundColor: 'rgba(255,119,0,0.1)' } }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon sx={{ color: '#1565c0', fontSize: 32 }} />
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#2E4057' }}>
                GST Status
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Hotels awaiting GST verification
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip
            label={`${list.length} pending`}
            color={list.length > 0 ? 'warning' : 'default'}
            sx={{ fontWeight: 600 }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchList({ notify: true })}
            disabled={loading}
            sx={{ borderColor: '#FF7700', color: '#FF7700' }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          label="Search"
          placeholder="Hotel ID, name, GST number, email, phone, or city"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')} edge="end" aria-label="Clear search">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 700, color: '#2E4057', width: '9%' }}>Hotel ID</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Hotel Name</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#2E4057', width: '12%' }}>GST Number</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#2E4057', width: '13%' }}>GST Status</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#2E4057', width: '16%' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#2E4057', width: '11%' }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#2E4057', width: '10%' }}>City</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredList.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  {list.length === 0 ? 'No pending GST hotels.' : 'No hotels match your search.'}
                </TableCell>
              </TableRow>
            )}
            {filteredList.map((row) => {
              const status = normalizeGstStatus(row);
              const isUpdating = updatingId === row.hotel_id;

              return (
                <TableRow key={row.hotel_id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{row.hotel_id ?? '–'}</TableCell>
                  <TableCell>{row.hotel_name || '–'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{row.gst_number || '–'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Select
                        size="small"
                        value={status}
                        disabled={isUpdating}
                        onChange={(e) => handleStatusChange(row, e.target.value)}
                        sx={{
                          minWidth: 130,
                          '& .MuiSelect-select': {
                            color: getGstStatusColor(status),
                            fontWeight: 700,
                          },
                        }}
                      >
                        <MenuItem value="pending" sx={{ color: '#ed6c02', fontWeight: 700 }}>Pending</MenuItem>
                        <MenuItem value="active" sx={{ color: '#2e7d32', fontWeight: 700 }}>Active</MenuItem>
                        <MenuItem value="rejected" sx={{ color: '#d32f2f', fontWeight: 700 }}>Rejected</MenuItem>
                      </Select>
                      {isUpdating && <CircularProgress size={16} />}
                    </Box>
                  </TableCell>
                  <TableCell>{row.email || '–'}</TableCell>
                  <TableCell>{row.phone || '–'}</TableCell>
                  <TableCell>{row.city_name || '–'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GstStatus;
